import type { AdminMoneyHealthDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AllClearPanel } from './_components/AllClearPanel';
import { BlindSpotIcon } from './_components/icons';
import {
  MoneyHealthScanCappedBanner,
  MoneyHealthStaleBanner,
} from './_components/MoneyHealthBanners';
import { MoneyHealthColumns } from './_components/MoneyHealthColumns';
import { MoneyHealthHeader } from './_components/MoneyHealthHeader';
import { SectionMeta } from './_components/SectionMeta';
import { SignalBar } from './_components/SignalBar';
import { scanText } from './_lib/moneyHealthText';
import { isForbiddenError, MONEY_HEALTH_REFETCH_MS, useAdminMoneyHealth } from '../../api';
import { RequirePermission } from '../../auth';
import { formatDateTime } from '../../utils/format';

/**
 * `/money-health` — «Нужно внимание» (AUT-328).
 *
 * Four money signals in one polled read of `GET /admin/money-health`. Phase 1
 * is a READ: there is no action anywhere on this page, and each row links out
 * to the surface that owns the action. Before an alerting channel can exist,
 * somebody has to watch a live queue for a week — rows that appear daily and
 * clear themselves must never reach a bot, and which ones those are is only
 * learnable by looking.
 *
 * ## The five rules this composition enforces
 *
 * **1. The permission gate wraps OUTSIDE the component that queries.**
 * `RequirePermission` renders its fallback INSTEAD of children, so the inner
 * component never mounts, so `useQuery` never runs, so an operator without
 * `process_payouts` issues zero requests. A gate placed inside would mount the
 * hook and start a 403 poll loop. (The nav row is NOT gated — no row in this
 * panel is, and three finance rows already 403 for such an operator.)
 *
 * **2. A mid-session 403 is an inline state, not a toast.** The JWT-derived
 * permission set is up to 15 minutes stale, so the gate above can pass while
 * the server says no. The query opts out of the shared toast and halts its
 * poll; this page says what happened, once.
 *
 * **3. Last-good data survives a failed refetch.** TanStack keeps `data`; a
 * blip renders ONE staleness banner over the real numbers rather than four
 * identical error alerts where four sections used to be.
 *
 * **4. Empty is stated, not implied.** All four signals at zero renders the
 * all-clear panel — an affirmative sentence plus what was checked. Four «Нет
 * данных» cards read as a broken screen.
 *
 * **5. The blind spot is always on screen.** `holdScan.capped` means both hold
 * counts are floors; that gets an `info` banner (never an error — nothing is
 * wrong, we just did not look at everything). Uncapped, the same fact renders
 * as a quiet footnote saying the counters are exact.
 */
const REFRESH_SECONDS = MONEY_HEALTH_REFETCH_MS / 1000;

const isAllClear = (data: AdminMoneyHealthDto): boolean =>
  data.escalatedHolds.total === 0 &&
  data.closingHolds.total === 0 &&
  data.failedPayouts.total === 0 &&
  data.stuckWebhooks.rowCount === 0;

/**
 * First paint with nothing cached: a 403 the client gate did not predict, a
 * server that did not answer, or the read still in flight. All three are page
 * states — never a toast, because this query polls.
 */
const FirstPaintState = ({ error }: { error: unknown }): ReactElement => {
  const { t } = useTranslation();
  if (isForbiddenError(error)) {
    return (
      <Alert severity="error" data-testid="money-health-forbidden">
        <AlertTitle>{t('moneyHealth.forbidden.title')}</AlertTitle>
        {t('moneyHealth.forbidden.body')}
      </Alert>
    );
  }
  if (error !== null) {
    return (
      <Alert severity="error" data-testid="money-health-error">
        <AlertTitle>{t('moneyHealth.error.title')}</AlertTitle>
        {t('moneyHealth.error.body')}
      </Alert>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 6, justifyContent: 'center' }}>
      <CircularProgress size={20} />
      <Box component="span">{t('moneyHealth.loading')}</Box>
    </Box>
  );
};

const MoneyHealthPageInner = (): ReactElement => {
  const query = useAdminMoneyHealth();
  const data = query.data;

  if (data === undefined) return <FirstPaintState error={query.error} />;

  const computedAtLabel = formatDateTime(data.computedAt);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <MoneyHealthHeader computedAtLabel={computedAtLabel} refreshSeconds={REFRESH_SECONDS} />

      {query.isError ? (
        <MoneyHealthStaleBanner error={query.error} computedAtLabel={computedAtLabel} />
      ) : null}

      {data.holdScan.capped ? <MoneyHealthScanCappedBanner scan={data.holdScan} /> : null}

      {isAllClear(data) ? (
        <AllClearPanel data={data} refreshSeconds={REFRESH_SECONDS} checkedAt={computedAtLabel} />
      ) : (
        <>
          <SignalBar data={data} />
          <MoneyHealthColumns data={data} />
          {/* An uncapped scan still says what was looked at — «пусто» and «не
              смотрели» are different answers. */}
          {data.holdScan.capped ? null : (
            <SectionMeta
              text={scanText(data.holdScan)}
              icon={<BlindSpotIcon />}
              testId="money-health-scan-note"
            />
          )}
        </>
      )}
    </Box>
  );
};

const Observed = observer(MoneyHealthPageInner);

/**
 * Whole-page gate — see rule 1 above. The inner component (and its query) does
 * not mount without `process_payouts`.
 */
export const MoneyHealthPage = (): ReactElement => (
  <RequirePermission perm="process_payouts">
    <Observed />
  </RequirePermission>
);
